import React, { useState, useEffect } from 'react';
import './TripDetailsScreen.css';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { db, auth } from './firebase';
import {
  collection,
  addDoc,
  serverTimestamp,
  GeoPoint,
  Timestamp,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { MIN_FARE } from './fareCalculator';

const TripDetailsScreen = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  console.log('🔍 TripDetailsScreen loaded');
  console.log('📍 State received:', state);

  const [loading, setLoading] = useState(false);
  const [noDriversOnline, setNoDriversOnline] = useState(false);
  const [noScheduledDrivers, setNoScheduledDrivers] = useState(false);
  const [driverConfirmed, setDriverConfirmed] = useState(false);
  const [isBookingInProgress, setIsBookingInProgress] = useState(false);
  const [currentSelectedTime, setCurrentSelectedTime] = useState(null);
  const [showAlternativesDialog, setShowAlternativesDialog] = useState(false);
  const [alternativeTimes, setAlternativeTimes] = useState([]);
  
  // Promo code functionality - STEP 1: RESTORE STATE MANAGEMENT
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [originalFare, setOriginalFare] = useState(0);
  const [discountedFare, setDiscountedFare] = useState(0);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoMessage, setPromoMessage] = useState('');

  const {
    pickup,
    dropoff,
    pickupLatLng,
    dropoffLatLng,
    name,
    phone,
    email,
    distanceMiles,
    fare,
    tripTime,
    tariffName,
    bookingMode,
    scheduledTime,
    numberOfRiders,
    wheelchairRequired,
  } = state || {};

  const requiredVehicleCapacity = Math.max(Number(numberOfRiders) || 1, 1);

  const getDriverVehicleCapacity = (driver) => {
    const capacity = Number(driver?.vehicleCapacity);
    return Number.isFinite(capacity) && capacity > 0 ? capacity : 4;
  };

  const driverMatchesRideRequirements = (driver) => {
    if (getDriverVehicleCapacity(driver) < requiredVehicleCapacity) {
      return false;
    }

    if (wheelchairRequired && !driver?.wheelchairAccessible) {
      return false;
    }

    return true;
  };

  // Initialize fare states when fare is available
  useEffect(() => {
    if (fare && originalFare === 0) {
      setOriginalFare(fare);
      setDiscountedFare(fare);
    }
  }, [fare, originalFare]);

  // Safety check for required data
  console.log('🔍 Checking required data:');
  console.log('- state:', !!state);
  console.log('- pickup:', pickup);
  console.log('- dropoff:', dropoff);
  console.log('- fare:', fare);

  if (!state || !pickup || !dropoff || !fare) {
    console.log('❌ Missing required data, showing error screen');
    return (
      <div className="trip-container">
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: '#dc3545'
        }}>
          <h2>❌ Missing Trip Data</h2>
          <p>Required trip information is missing. Please go back and try again.</p>
          <p style={{fontSize: '12px', color: '#666', marginTop: '10px'}}>
            Debug: state={!!state ? 'yes' : 'no'}, pickup={pickup || 'missing'}, dropoff={dropoff || 'missing'}, fare={fare || 'missing'}
          </p>
          <button 
            className="trip-button"
            onClick={() => navigate('/map')}
            style={{ marginTop: '20px' }}
          >
            🔙 Back to Map
          </button>
        </div>
      </div>
    );
  }

  console.log('✅ Required data OK, continuing with component');

  const showToast = (message, type = 'default') => {
    try {
      toast(message, { type });
    } catch (error) {
      console.error('Toast error:', error);
    }
  };

  // Utility functions for smart alternatives
  const getDayName = (date) => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    return days[date.getDay() === 0 ? 6 : date.getDay() - 1]; // Convert Sunday=0 to Monday=0
  };

  const timeStringToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const isTimeInRange = (timeToCheck, startTime, finishTime) => {
    const checkMinutes = timeStringToMinutes(timeToCheck);
    const startMinutes = timeStringToMinutes(startTime);
    const finishMinutes = timeStringToMinutes(finishTime);
    return checkMinutes >= startMinutes && checkMinutes <= finishMinutes;
  };

  const minutesToDateTime = (baseDate, minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), hours, mins);
  };

  const estimateTravelTime = (fromLatLng, toLatLng) => {
    if (!fromLatLng || !toLatLng) return 10; // Default buffer
    
    try {
      // Simple distance-based estimation (you could enhance with Google Directions API)
      const earthRadius = 6371; // km
      const dLat = (toLatLng.lat - fromLatLng.lat) * (Math.PI / 180);
      const dLon = (toLatLng.lng - fromLatLng.lng) * (Math.PI / 180);
      
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(fromLatLng.lat * (Math.PI / 180)) * Math.cos(toLatLng.lat * (Math.PI / 180)) *
          Math.sin(dLon / 2) * Math.sin(dLon / 2);
      
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = earthRadius * c;
      
      // Rough estimation: 2 minutes per km in urban areas
      return Math.round(distance * 2).clamp ? Math.round(distance * 2).clamp(5, 30) : Math.max(5, Math.min(30, Math.round(distance * 2)));
    } catch (e) {
      return 10; // Default if calculation fails
    }
  };

  const hasConflictWithOtherBookings = (proposedStart, proposedDuration, allBookings, excludeBooking) => {
    const proposedEnd = proposedStart + proposedDuration;
    
    for (const booking of allBookings) {
      if (excludeBooking && booking === excludeBooking) continue;
      
      const bookingStart = booking.scheduledTime.getHours() * 60 + booking.scheduledTime.getMinutes();
      const estimatedTripTime = typeof booking.estimatedTripTime === 'number' ? booking.estimatedTripTime : 30;
      const bookingEnd = bookingStart + estimatedTripTime;
      
      // Check for overlap with 15-minute buffer
      if ((proposedEnd + 15 > bookingStart - 15) && (proposedStart - 15 < bookingEnd + 15)) {
        return true;
      }
    }
    
    return false;
  };

  const calculateSmartAlternatives = (workStartMinutes, workEndMinutes, existingBookings, requestedTime, newTripDuration) => {
    const alternatives = [];
    const requestedMinutes = requestedTime.getHours() * 60 + requestedTime.getMinutes();

    // Sort bookings by time
    existingBookings.sort((a, b) => a.scheduledTime.getTime() - b.scheduledTime.getTime());

    // Find the closest booking that conflicts with our requested time
    let conflictingBooking = null;
    for (const booking of existingBookings) {
      const bookingStart = booking.scheduledTime.getHours() * 60 + booking.scheduledTime.getMinutes();
      const estimatedTripTime = typeof booking.estimatedTripTime === 'number' ? booking.estimatedTripTime : 30;
      const bookingEnd = bookingStart + estimatedTripTime;
      
      // Check if this booking conflicts with our requested time (with 15-minute buffer)
      if ((requestedMinutes + newTripDuration + 15 > bookingStart - 15) && 
          (requestedMinutes - 15 < bookingEnd + 15)) {
        conflictingBooking = booking;
        break;
      }
    }

    if (conflictingBooking) {
      const conflictStart = conflictingBooking.scheduledTime.getHours() * 60 + conflictingBooking.scheduledTime.getMinutes();
      const conflictTripTime = typeof conflictingBooking.estimatedTripTime === 'number' ? conflictingBooking.estimatedTripTime : 30;
      const conflictEnd = conflictStart + conflictTripTime;

      // Calculate travel time between jobs for realistic scheduling
      const travelTimeToConflict = estimateTravelTime(
        { lat: dropoffLatLng.lat, lng: dropoffLatLng.lng },
        conflictingBooking.pickupLatLng
      );
      
      const travelTimeFromConflict = estimateTravelTime(
        conflictingBooking.dropoffLatLng,
        { lat: pickupLatLng.lat, lng: pickupLatLng.lng }
      );

      // OPTION 1: Suggest time BEFORE the conflicting booking
      const beforeSlotEnd = conflictStart - 15 - travelTimeToConflict;
      const beforeSlotStart = beforeSlotEnd - newTripDuration;
      
      if (beforeSlotStart >= workStartMinutes && beforeSlotStart >= requestedMinutes - 120) { // Within 2 hours before requested
        const beforeTime = minutesToDateTime(requestedTime, beforeSlotStart);
        if (!hasConflictWithOtherBookings(beforeSlotStart, newTripDuration, existingBookings, conflictingBooking)) {
          alternatives.push(beforeTime);
        }
      }

      // OPTION 2: Suggest time AFTER the conflicting booking
      const afterSlotStart = conflictEnd + 15 + travelTimeFromConflict;
      
      if (afterSlotStart + newTripDuration <= workEndMinutes && afterSlotStart <= requestedMinutes + 180) { // Within 3 hours after requested
        const afterTime = minutesToDateTime(requestedTime, afterSlotStart);
        if (!hasConflictWithOtherBookings(afterSlotStart, newTripDuration, existingBookings, conflictingBooking)) {
          alternatives.push(afterTime);
        }
      }
    } else {
      // No direct conflict found, suggest nearby available slots
      let previousBooking = null;
      let nextBooking = null;
      
      for (const booking of existingBookings) {
        const bookingStart = booking.scheduledTime.getHours() * 60 + booking.scheduledTime.getMinutes();
        
        if (bookingStart < requestedMinutes) {
          previousBooking = booking; // Keep updating to get the latest one before
        } else if (bookingStart > requestedMinutes && !nextBooking) {
          nextBooking = booking; // Get the first one after
          break;
        }
      }

      // Suggest a slot before the next booking if available
      if (nextBooking) {
        const nextStart = nextBooking.scheduledTime.getHours() * 60 + nextBooking.scheduledTime.getMinutes();
        const travelTime = estimateTravelTime(
          { lat: dropoffLatLng.lat, lng: dropoffLatLng.lng },
          nextBooking.pickupLatLng
        );
        
        const beforeNextEnd = nextStart - 15 - travelTime;
        const beforeNextStart = beforeNextEnd - newTripDuration;
        
        if (beforeNextStart >= workStartMinutes && beforeNextStart !== requestedMinutes) {
          alternatives.push(minutesToDateTime(requestedTime, beforeNextStart));
        }
      }

      // Suggest a slot after the previous booking if available
      if (previousBooking) {
        const prevStart = previousBooking.scheduledTime.getHours() * 60 + previousBooking.scheduledTime.getMinutes();
        const prevTripTime = typeof previousBooking.estimatedTripTime === 'number' ? previousBooking.estimatedTripTime : 30;
        const prevEnd = prevStart + prevTripTime;
        const travelTime = estimateTravelTime(
          previousBooking.dropoffLatLng,
          { lat: pickupLatLng.lat, lng: pickupLatLng.lng }
        );
        
        const afterPrevStart = prevEnd + 15 + travelTime;
        
        if (afterPrevStart + newTripDuration <= workEndMinutes && afterPrevStart !== requestedMinutes) {
          alternatives.push(minutesToDateTime(requestedTime, afterPrevStart));
        }
      }
    }

    // If no smart alternatives found, suggest one safe slot within working hours
    if (alternatives.length === 0) {
      // Try to find a safe slot 1 hour before or after the requested time
      const oneHourBefore = requestedMinutes - 60;
      const oneHourAfter = requestedMinutes + 60;
      
      if (oneHourBefore >= workStartMinutes && 
          !hasConflictWithOtherBookings(oneHourBefore, newTripDuration, existingBookings, null)) {
        alternatives.push(minutesToDateTime(requestedTime, oneHourBefore));
      }
      
      if (oneHourAfter + newTripDuration <= workEndMinutes && 
          !hasConflictWithOtherBookings(oneHourAfter, newTripDuration, existingBookings, null)) {
        alternatives.push(minutesToDateTime(requestedTime, oneHourAfter));
      }
    }

    // Limit to maximum 2 alternatives as requested
    return alternatives.slice(0, 2);
  };

  const scrollToTop = () => {
    // Try multiple methods for better browser compatibility
    try {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    } catch (e) {
      // Fallback for older browsers
      window.scrollTo(0, 0);
    }
    
    // Alternative method using document
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  // Promo code functionality with enhanced tracking
  const validatePromoCode = async (code) => {
    if (!code || code.trim() === '') {
      setPromoMessage('Please enter a promo code');
      showToast('Please enter a promo code', 'warning');
      return false;
    }

    setPromoLoading(true);
    setPromoMessage('');
    
    try {
      const promoQuery = query(
        collection(db, 'promoCodes'),
        where('code', '==', code.toUpperCase().trim()),
        where('isActive', '==', true)
      );
      
      const promoSnap = await getDocs(promoQuery);
      
      if (promoSnap.empty) {
        setPromoMessage('❌ Invalid promo code');
        showToast('❌ Invalid promo code', 'error');
        setPromoLoading(false);
        return false;
      }

      const promoDoc = promoSnap.docs[0];
      const promoData = promoDoc.data();
      const now = new Date();
      
      // Check if promo code is expired
      if (promoData.expiryDate && promoData.expiryDate.toDate() < now) {
        setPromoMessage('❌ This promo code has expired');
        showToast('❌ This promo code has expired', 'error');
        setPromoLoading(false);
        return false;
      }

      // Check if promo code has usage limit
      if (promoData.usageLimit && (promoData.timesUsed || 0) >= promoData.usageLimit) {
        setPromoMessage('❌ This promo code has reached its usage limit');
        showToast('❌ This promo code has reached its usage limit', 'error');
        setPromoLoading(false);
        return false;
      }

      // 🆕 Check if current user has already used this promo code
      const currentUserId = auth.currentUser?.uid || localStorage.getItem('guestUid');
      if (currentUserId && promoData.usedByUsers && promoData.usedByUsers.includes(currentUserId)) {
        setPromoMessage('❌ You have already used this promo code');
        showToast('❌ You have already used this promo code', 'error');
        setPromoLoading(false);
        return false;
      }

      // Check minimum fare requirement
      if (promoData.minFare && originalFare < promoData.minFare) {
        setPromoMessage(`❌ Minimum fare of £${promoData.minFare} required for this promo code`);
        showToast(`❌ Minimum fare of £${promoData.minFare} required for this promo code`, 'error');
        setPromoLoading(false);
        return false;
      }

      // Apply discount
      let discount = 0;
      if (promoData.discountType === 'percentage') {
        discount = originalFare * (promoData.discountValue / 100);
        if (promoData.maxDiscount) {
          discount = Math.min(discount, promoData.maxDiscount);
        }
      } else if (promoData.discountType === 'fixed') {
        discount = promoData.discountValue;
      }

      const newFare = Math.max(originalFare - discount, promoData.minFinalFare || 0);
      
      setAppliedPromo({
        ...promoData,
        id: promoDoc.id,
        discount: discount,
        originalFare: originalFare,
        newFare: newFare
      });
      
      setDiscountedFare(newFare);
      setPromoMessage(`✅ Promo code applied! You saved £${discount.toFixed(2)}`);
      showToast(`✅ Promo code applied! You saved £${discount.toFixed(2)}`, 'success');
      setPromoLoading(false);
      return true;
      
    } catch (error) {
      console.error('Error validating promo code:', error);
      setPromoMessage('Error validating promo code');
      showToast('Error validating promo code', 'error');
      setPromoLoading(false);
      return false;
    }
  };

  // 🆕 Function to update promo code usage tracking
  const updatePromoCodeUsage = async (promoCodeId, userId) => {
    try {
      const promoRef = doc(db, 'promoCodes', promoCodeId);
      const promoDoc = await getDoc(promoRef);
      
      if (promoDoc.exists()) {
        const currentData = promoDoc.data();
        const currentTimesUsed = currentData.timesUsed || 0;
        const currentUsedByUsers = currentData.usedByUsers || [];
        
        // Add user to the list if not already present
        const updatedUsedByUsers = currentUsedByUsers.includes(userId) 
          ? currentUsedByUsers 
          : [...currentUsedByUsers, userId];
        
        await updateDoc(promoRef, {
          timesUsed: currentTimesUsed + 1,
          usedByUsers: updatedUsedByUsers,
          lastUsedAt: serverTimestamp()
        });
        
        console.log(`✅ Promo code ${currentData.code} usage updated: ${currentTimesUsed + 1} times used`);
      }
    } catch (error) {
      console.error('Error updating promo code usage:', error);
      // Don't block the ride booking if promo update fails
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    setPromoCode('');
    setDiscountedFare(originalFare);
    showToast('Promo code removed', 'info');
  };

  const checkDriverAvailability = async () => {
    console.log('🚀 checkDriverAvailability function called');
    showToast(`🔍 Checking drivers... (Mode: ${bookingMode === 'asap' ? 'Ride Now' : 'Ride Later'})`, 'info');
    
    setLoading(true);
    setNoDriversOnline(false);
    setNoScheduledDrivers(false);

    // Scroll to top so user can see status messages (with small delay to ensure DOM is updated)
    setTimeout(() => {
      scrollToTop();
    }, 100);

    try {
      const driversSnap = await getDocs(collection(db, 'drivers'));
      const drivers = driversSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const eligibleDrivers = drivers.filter(driverMatchesRideRequirements);

      if (eligibleDrivers.length === 0) {
        const requirementMessage = wheelchairRequired
          ? '🚫 No wheelchair-accessible vehicle is configured for this booking right now.'
          : `🚫 No vehicle with space for ${requiredVehicleCapacity} riders is configured right now.`;
        showToast(requirementMessage, 'warning');
        setLoading(false);
        return;
      }

      if (bookingMode === 'scheduled') {
        if (!scheduledTime || isNaN(new Date(scheduledTime).getTime())) {
          showToast('⚠️ Invalid scheduled time. Please select a valid date and time.', 'warning');
          setLoading(false);
          return;
        }

        const bookingDate = new Date(scheduledTime);
        const lowerBound = new Date(bookingDate.getTime() - 30 * 60000);
        const upperBound = new Date(bookingDate.getTime() + 30 * 60000);

        const dayName = getDayName(bookingDate);
        const timeStr = bookingDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
        const dateStr = bookingDate.toISOString().split('T')[0];

        const availableDrivers = [];

        // For scheduled rides, check ALL drivers (not just online ones)
        for (const driver of eligibleDrivers) {
          const availability = driver.availability || [];
          const unavailableDates = driver.unavailableDates || [];

          if (unavailableDates.includes(dateStr)) continue;

          const dayMatch = availability.find(day => {
            const startTime = day.start?.toString() || '';
            const finishTime = day.finish?.toString() || '';
            const dayNameMatch = day.day?.toString().toLowerCase() || '';
            
            return dayNameMatch === dayName && isTimeInRange(timeStr, startTime, finishTime);
          });

          if (dayMatch) {
            // Check for conflicting rides
            const conflictingSnap = await getDocs(
              query(
                collection(db, 'riders'),
                where('assignedDriverId', '==', driver.id),
                where('scheduledTime', '>=', Timestamp.fromDate(lowerBound)),
                where('scheduledTime', '<=', Timestamp.fromDate(upperBound)),
                where('status', 'in', ['pending', 'accepted', 'awaiting_driver_response'])
              )
            );

            if (conflictingSnap.empty) {
              availableDrivers.push(driver);
            }
          }
        }

        if (availableDrivers.length === 0) {
          // Instead of just saying no drivers available, offer alternative times
          await findAlternativeTimeSlots(eligibleDrivers, bookingDate);
          return;
        }

        const assignedDriver = availableDrivers[0];
        await submitRideRequest(assignedDriver);
        setDriverConfirmed(true);
        showToast(`✅ Driver ${assignedDriver.driverName || assignedDriver.name} assigned for your scheduled ride.`, 'success');
        setLoading(false);
        return;
      }

      // Handle ASAP rides - these still need online drivers
      const online = eligibleDrivers.filter(d => d.isOnline);
      const free = online.filter(d => !d.isOnJob);

      console.log(`=== DRIVER AVAILABILITY DEBUG ===`);
      console.log(`Total drivers: ${drivers.length}, Eligible: ${eligibleDrivers.length}, Online: ${online.length}, Free: ${free.length}`);
      console.log(`Booking mode: ${bookingMode === 'asap' ? 'Ride Now' : 'Ride Later'}`);
      
      // Add visual debug right in the UI
      showToast(`📊 DEBUG: Drivers=${drivers.length}, Eligible=${eligibleDrivers.length}, Online=${online.length}, Free=${free.length}`, 'info');
      
      // Debug each driver's status
      eligibleDrivers.forEach((driver, index) => {
        console.log(`Driver ${index + 1}: ${driver.driverName || 'Unknown'}`);
        console.log(`  - isOnline: ${driver.isOnline}`);
        console.log(`  - isOnJob: ${driver.isOnJob}`);
        console.log(`  - vehicleCapacity: ${getDriverVehicleCapacity(driver)}`);
        console.log(`  - wheelchairAccessible: ${!!driver.wheelchairAccessible}`);
      });
      console.log(`================================`);

      if (online.length === 0) {
        console.log('❌ CONDITION: No drivers online (online = 0)');
        showToast('🔍 DEBUG: Taking NO DRIVERS ONLINE path', 'info');
        // No drivers online at all
        setNoDriversOnline(true);
        setNoScheduledDrivers(false); // Clear this flag
        showToast(
          wheelchairRequired
            ? '🚫 No wheelchair-accessible drivers are currently online. Please try again later or schedule a ride.'
            : `🚫 No drivers with space for ${requiredVehicleCapacity} riders are currently online. Please try again later or schedule a ride.`,
          'error'
        );
      } else if (free.length === 0) {
        console.log('❌ CONDITION: All online drivers busy (online > 0, free = 0)');
        showToast('🔍 DEBUG: Taking ALL DRIVERS BUSY path', 'info');
        // All online drivers are busy - use different flag for this case
        setNoDriversOnline(false); // Clear this flag
        setNoScheduledDrivers(true); // Use this flag for busy drivers
        showToast(
          wheelchairRequired
            ? '🚫 Sorry, all wheelchair-accessible drivers are busy on rides right now. Please try again later.'
            : `🚫 Sorry, all drivers with space for ${requiredVehicleCapacity} riders are busy on rides right now. Please try again later.`,
          'warning'
        );
      } else {
        console.log('✅ CONDITION: Free drivers available, proceeding with booking');
        showToast('🔍 DEBUG: Taking FREE DRIVERS AVAILABLE path', 'info');
        // We have free drivers available - proceed with booking
        console.log('✅ Free drivers available, submitting ride request');
        const matchedDriver = wheelchairRequired || requiredVehicleCapacity > 4 ? free[0] : null;
        await submitRideRequest(matchedDriver);
        setLoading(false);
        return; // Exit here so we don't set loading to false again
      }
    } catch (err) {
      console.error('Driver availability error:', err);
      showToast('Something went wrong checking drivers.', 'error');
    }

    setLoading(false);
  };

  const findAlternativeTimeSlots = async (drivers, requestedTime) => {
    const dateStr = requestedTime.toISOString().split('T')[0];
    const suggestedTimes = [];

    try {
      for (const driver of drivers) {
        const availability = driver.availability || [];
        const unavailableDates = driver.unavailableDates || [];

        // Skip if driver is unavailable on this date
        if (unavailableDates.includes(dateStr)) continue;

        // Check if driver works on this day
        const dayAvailability = availability.find(day => 
          day.day?.toString().toLowerCase() === getDayName(requestedTime)
        );

        if (!dayAvailability) continue;

        const startTime = dayAvailability.start?.toString() || '';
        const finishTime = dayAvailability.finish?.toString() || '';
        const workStartMinutes = timeStringToMinutes(startTime);
        const workEndMinutes = timeStringToMinutes(finishTime);

        // Get existing bookings for this driver on this date
        const startOfDay = new Date(requestedTime.getFullYear(), requestedTime.getMonth(), requestedTime.getDate());
        const endOfDay = new Date(requestedTime.getFullYear(), requestedTime.getMonth(), requestedTime.getDate(), 23, 59);

        const existingBookingsSnap = await getDocs(
          query(
            collection(db, 'riders'),
            where('assignedDriverId', '==', driver.id),
            where('scheduledTime', '>=', Timestamp.fromDate(startOfDay)),
            where('scheduledTime', '<=', Timestamp.fromDate(endOfDay)),
            where('status', 'in', ['pending', 'accepted', 'awaiting_driver_response'])
          )
        );

        const bookings = existingBookingsSnap.docs.map(doc => {
          const data = doc.data();
          return {
            scheduledTime: data.scheduledTime.toDate(),
            estimatedTripTime: data.estimatedTripTime || 30,
            pickupLatLng: data.pickupLatLng,
            dropoffLatLng: data.dropoffLatLng,
            pickupAddress: data.pickupAddress || '',
            dropoffAddress: data.dropoffAddress || '',
          };
        });

        // Calculate smart alternative time slots (only 2 - before and after existing bookings)
        const newTripDuration = Math.round(tripTime);
        const alternatives = calculateSmartAlternatives(
          workStartMinutes, 
          workEndMinutes, 
          bookings, 
          requestedTime, 
          newTripDuration
        );

        suggestedTimes.push(...alternatives);
      }

      // Remove duplicates and sort
      const uniqueTimes = [...new Set(suggestedTimes.map(t => t.getTime()))].map(t => new Date(t));
      uniqueTimes.sort((a, b) => a.getTime() - b.getTime());

      if (uniqueTimes.length > 0) {
        setAlternativeTimes(uniqueTimes.slice(0, 2)); // Limit to 2 alternatives
        setShowAlternativesDialog(true);
        setLoading(false);
      } else {
        setNoScheduledDrivers(true);
        setLoading(false);
        showToast('🚫 Sorry, no drivers are available for the selected date and time. No alternative slots found.', 'error');
      }

    } catch (e) {
      setNoScheduledDrivers(true);
      setLoading(false);
      showToast(`Error finding alternative times: ${e.message}`, 'error');
    }
  };

  const bookAlternativeTime = async (selectedTime) => {
    console.log('Starting booking process for alternative time:', selectedTime.toLocaleTimeString());
    
    setIsBookingInProgress(true);
    setLoading(true);
    setNoScheduledDrivers(false);

    try {
      const driversSnap = await getDocs(collection(db, 'drivers'));
      const drivers = driversSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const dateStr = selectedTime.toISOString().split('T')[0];
      const timeStr = selectedTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
      
      // Use a smaller buffer for alternative bookings (15 minutes instead of 30)
      const lowerBound = new Date(selectedTime.getTime() - 15 * 60000);
      const upperBound = new Date(selectedTime.getTime() + 15 * 60000);

      console.log('Trying to book alternative time:', timeStr, 'on', dateStr);

      // Find an available driver for this specific time slot
      for (const driver of drivers) {
        const availability = driver.availability || [];
        const unavailableDates = driver.unavailableDates || [];
        const driverName = driver.driverName || driver.name || 'Unknown Driver';

        console.log('Checking driver:', driverName, `(${driver.id})`);

        // Skip if driver is unavailable on this date
        if (unavailableDates.includes(dateStr)) {
          console.log(`Driver ${driverName} is unavailable on ${dateStr}`);
          continue;
        }

        // Check if driver works on this day and time
        const dayMatch = availability.find(day => {
          const startTime = day.start?.toString() || '';
          const finishTime = day.finish?.toString() || '';
          const dayName = day.day?.toString().toLowerCase() || '';
          
          const matches = dayName === getDayName(selectedTime) && isTimeInRange(timeStr, startTime, finishTime);
          
          if (matches) {
            console.log(`Driver ${driverName} is available on ${getDayName(selectedTime)} from ${startTime} to ${finishTime}`);
          }
          
          return matches;
        });

        if (dayMatch) {
          // Check for conflicting rides with a more lenient approach
          const conflictingSnap = await getDocs(
            query(
              collection(db, 'riders'),
              where('assignedDriverId', '==', driver.id),
              where('scheduledTime', '>=', Timestamp.fromDate(lowerBound)),
              where('scheduledTime', '<=', Timestamp.fromDate(upperBound)),
              where('status', 'in', ['pending', 'accepted', 'awaiting_driver_response'])
            )
          );

          console.log(`Driver ${driverName} has ${conflictingSnap.docs.length} conflicting rides in the time window`);

          if (conflictingSnap.empty) {
            console.log(`Found available driver: ${driverName} for time ${timeStr}`);
            
            // Update the current selected time for UI display
            setCurrentSelectedTime(selectedTime);
            
            // Found an available driver! Book immediately
            await submitRideRequestWithTime(driver, selectedTime);
            setDriverConfirmed(true);
            setLoading(false);
            setIsBookingInProgress(false);
            setNoScheduledDrivers(false);
            setNoDriversOnline(false);
            setShowAlternativesDialog(false);
            
            console.log('✅ BOOKING SUCCESS: Driver confirmed, clearing all error states');
            
            const timeString = selectedTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
            showToast(`🎉 Booking confirmed! Driver: ${driverName}, Time: ${timeString}`, 'success');
            
            return; // Make sure we exit the function here
          } else {
            console.log(`Driver ${driverName} has conflicts:`);
            conflictingSnap.docs.forEach(doc => {
              const data = doc.data();
              const conflictTime = data.scheduledTime.toDate();
              console.log(`  - Conflict at ${conflictTime.toLocaleTimeString()}`);
            });
          }
        } else {
          console.log(`Driver ${driverName} is not available on ${getDayName(selectedTime)} at ${timeStr}`);
        }
      }

      // If we get here, no driver was available
      console.log('No drivers available for the selected alternative time');
      
      if (!driverConfirmed) {
        setLoading(false);
        setNoScheduledDrivers(true);
        setIsBookingInProgress(false);
        showToast('Sorry, the selected time slot is no longer available. Please try another time.', 'warning');
      }

    } catch (e) {
      console.log('Error booking alternative time:', e);
      
      if (!driverConfirmed) {
        setLoading(false);
        setNoScheduledDrivers(true);
        setIsBookingInProgress(false);
        showToast(`Error booking alternative time: ${e.message}`, 'error');
      }
    }
  };

  const submitRideRequestWithTime = async (driver, scheduledTime) => {
    try {
      console.log('🚀 Starting scheduled ride submission...');
      console.log('Driver:', driver);
      console.log('Scheduled time:', scheduledTime);
      console.log('Email:', email);
      console.log('Name:', name);
      console.log('Phone:', phone);
      
      // Validate required fields
      if (!email) {
        showToast('❌ Email is required for scheduled rides', 'error');
        return;
      }
      if (!name) {
        showToast('❌ Name is required', 'error');
        return;
      }
      if (!phone) {
        showToast('❌ Phone is required', 'error');
        return;
      }
      
      // Determine the passenger ID and type (authenticated or guest)
      let passengerIdToUse = '';
      let passengerType = 'authenticated';

      if (auth.currentUser?.uid) {
        passengerIdToUse = auth.currentUser.uid;
      } else {
        const storedUserType = localStorage.getItem('userType');
        if (storedUserType === 'guest') {
          const storedGuestUid = localStorage.getItem('guestUid');
          if (storedGuestUid) {
            passengerIdToUse = storedGuestUid;
            passengerType = 'guest';
          }
        }
      }

      const roundedTripTime = Math.round(tripTime);
      const driverData = driver;

  const effectiveFare = appliedPromo ? Math.max(discountedFare, MIN_FARE) : Math.max(fare, MIN_FARE);
  const effectiveOriginalFare = appliedPromo ? Math.max(originalFare, MIN_FARE) : Math.max(fare, MIN_FARE);
  const rideDoc = {
        passengerId: passengerIdToUse,
        passengerType: passengerType,
        pickupAddress: pickup,
        dropoffAddress: dropoff,
        pickup: pickup,  // For email functions
        destination: dropoff,  // For email functions
        pickupLatLng: new GeoPoint(pickupLatLng.lat, pickupLatLng.lng),
        dropoffLatLng: new GeoPoint(dropoffLatLng.lat, dropoffLatLng.lng),
        numberOfRiders: numberOfRiders || 1,
        wheelchairRequired: !!wheelchairRequired,
        requiredVehicleCapacity,
        status: 'awaiting_driver_response',
        assignedDriverId: driver.id,
        currentOfferTo: driver.id,
        timestamp: serverTimestamp(),
        distanceMiles,
        estimatedTripTime: roundedTripTime,
  calculatedFare: effectiveFare,
  originalFare: effectiveOriginalFare,
  estimatedFare: effectiveFare,  // For email functions
        promoCode: appliedPromo ? appliedPromo.code : null,
        promoDiscount: appliedPromo ? appliedPromo.discount : 0,
        passengerName: name,
        passengerPhone: phone,
        passengerEmail: email,  // For email functions
        driverName: driverData.driverName || driverData.name,
        driverVehicle: driverData.driverVehicle || driverData.vehicle,
        bookingMode: 'scheduled',
        isScheduled: true,  // For email functions
        scheduledTime: Timestamp.fromDate(scheduledTime),
        bookingId: `UGO-${Date.now()}`,  // For email functions
      };

      const rideRef = await addDoc(collection(db, 'riders'), rideDoc);
      console.log('✅ Ride request submitted successfully with ID:', rideRef.id);
      
      // 🆕 Update promo code usage if promo was applied
      if (appliedPromo && appliedPromo.id) {
        const currentUserId = auth.currentUser?.uid || localStorage.getItem('guestUid');
        if (currentUserId) {
          await updatePromoCodeUsage(appliedPromo.id, currentUserId);
        }
      }
      
      // Show success message and navigate
      showToast('✅ Scheduled ride submitted successfully! You will receive email confirmation.', 'success');
      
      // Navigate back to map or show confirmation
      setTimeout(() => {
        navigate('/map');
      }, 2000);

    } catch (e) {
      console.log('Error submitting ride:', e);
      showToast(`Failed to submit ride: ${e.message}`, 'error');
    }
  };

  const submitRideRequest = async (driver = null) => {
    try {
      console.log('🚀 Starting ride submission...');
      console.log('Email:', email);
      console.log('Name:', name);
      console.log('Phone:', phone);
      console.log('Booking Mode:', bookingMode);
      
      // Validate required fields for scheduled rides
      if (bookingMode === 'scheduled' && !email) {
        showToast('❌ Email is required for scheduled rides', 'error');
        return;
      }
      
      // 🧑‍✈️ Determine the passenger ID and type (authenticated or guest)
      let passengerIdToUse = '';
      let passengerType = 'authenticated';

      if (auth.currentUser?.uid) {
        passengerIdToUse = auth.currentUser.uid;
      } else {
        const storedUserType = localStorage.getItem('userType');
        if (storedUserType === 'guest') {
          const storedGuestUid = localStorage.getItem('guestUid');
          if (storedGuestUid) {
            passengerIdToUse = storedGuestUid;
            passengerType = 'guest';
          }
        }
      }

      // 🗺️ Compose ride request document
  const effectiveFare2 = appliedPromo ? Math.max(discountedFare, MIN_FARE) : Math.max(fare, MIN_FARE);
  const effectiveOriginalFare2 = appliedPromo ? Math.max(originalFare, MIN_FARE) : Math.max(fare, MIN_FARE);
  const rideDoc = {
    pickupAddress: pickup,
    dropoffAddress: dropoff,
    pickup: pickup,  // For email functions
    destination: dropoff,  // For email functions
    pickupLatLng: new GeoPoint(pickupLatLng.lat, pickupLatLng.lng),
    dropoffLatLng: new GeoPoint(dropoffLatLng.lat, dropoffLatLng.lng),
    numberOfRiders: numberOfRiders || 1,
    wheelchairRequired: !!wheelchairRequired,
    requiredVehicleCapacity,
  calculatedFare: effectiveFare2,
  originalFare: effectiveOriginalFare2,
  estimatedFare: effectiveFare2,  // For email functions
    promoCode: appliedPromo ? appliedPromo.code : null,
    promoDiscount: appliedPromo ? appliedPromo.discount : 0,
    estimatedTripTime: tripTime,
    distanceMiles,
    passengerName: name,
    passengerPhone: phone,
    passengerEmail: email,
    passengerId: passengerIdToUse,
    passengerType: passengerType,
    assignedDriverId: driver?.id || null,
    currentOfferTo: driver?.id || null, // 👈 ADD THIS
    driverName: driver?.driverName || driver?.name || null,
    driverVehicle: driver?.driverVehicle || driver?.vehicle || null,
    bookingMode: bookingMode || 'asap',
    isScheduled: bookingMode === 'scheduled',  // For email functions
    scheduledTime: bookingMode === 'scheduled'
      ? Timestamp.fromDate(new Date(scheduledTime))
      : null,
    status: bookingMode === 'scheduled'
      ? 'awaiting_driver_response'
      : 'pending',
    timestamp: serverTimestamp(),
    bookingId: `UGO-${Date.now()}`,  // For email functions
  };

      const rideRef = await addDoc(collection(db, 'riders'), rideDoc);

      // 🆕 Update promo code usage if promo was applied
      if (appliedPromo && appliedPromo.id) {
        const currentUserId = auth.currentUser?.uid || localStorage.getItem('guestUid');
        if (currentUserId) {
          await updatePromoCodeUsage(appliedPromo.id, currentUserId);
        }
      }

      // 🚦 Navigate only for ASAP rides
      if (bookingMode === 'asap') {
        navigate('/trip-tracking', { state: { rideId: rideRef.id } });
      }

      showToast('✅ Ride successfully submitted.', 'success');
    } catch (err) {
      console.error('Failed to submit ride:', err);
      showToast('Failed to submit ride.', 'error');
    }
  };

  return (
    <div className="trip-card">
      <img src="https://u-gotaxi.co.uk/react-widget/assets/logo_ugo_taxi.png" alt="U-Go Taxi" className="trip-logo" />
      <h2 className="trip-heading">Trip Confirmation</h2>
      <p className="trip-subtext">For assistance call: 07926 422711</p>

      {noDriversOnline && (
        <div className="driver-warning-banner" style={{ 
          background: '#f8d7da', 
          borderColor: '#dc3545', 
          color: '#721c24',
          textAlign: 'center',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #dc3545',
          marginBottom: '20px'
        }}>
          <div style={{ marginBottom: '15px' }}>
            <strong>🚫 No drivers are currently online</strong><br />
            Please try again later or schedule a ride for later.
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button 
              style={{
                background: '#28a745',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              onClick={() => {
                setNoDriversOnline(false);
                setLoading(false);
                checkDriverAvailability();
              }}
            >
              Try Again
            </button>
            <button 
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              onClick={() => auth.signOut().then(() => navigate('/'))}
            >
              Log Out
            </button>
          </div>
        </div>
      )}

      {noScheduledDrivers && bookingMode === 'asap' && (
        <div className="driver-warning-banner" style={{ 
          background: '#fff3cd', 
          borderColor: '#ffc107', 
          color: '#856404',
          textAlign: 'center',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #ffc107',
          marginBottom: '20px'
        }}>
          <div style={{ marginBottom: '15px' }}>
            <strong>⏰ All online drivers are busy on rides!</strong><br />
            Please try again later.
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button 
              style={{
                background: '#28a745',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              onClick={() => {
                setNoScheduledDrivers(false);
                setLoading(false);
                checkDriverAvailability();
              }}
            >
              Try Again
            </button>
            <button 
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              onClick={() => auth.signOut().then(() => navigate('/'))}
            >
              Log Out
            </button>
          </div>
        </div>
      )}

      {noScheduledDrivers && bookingMode === 'scheduled' && (
        <div className="driver-warning-banner">
          🚫 <strong>No drivers are available for the selected date and time.</strong><br />
          Please try another time slot
        </div>
      )}
      
      {driverConfirmed && (
      <div className="driver-success-banner">
        ✅ <strong>Driver found and assigned to accept.</strong><br />
        Please re-login to access your trip tracking screen.
        <button className="logout-button" onClick={() => auth.signOut().then(() => navigate('/'))}>
        Log Out
        </button>
      </div>
    )}


      <div className="trip-group"><label>Pickup:</label><span>{pickup}</span></div>
      <div className="trip-group"><label>Drop-off:</label><span>{dropoff}</span></div>
      <div className="trip-group"><label>Tariff:</label><span>{tariffName}</span></div>
      <div className="trip-group"><label>Distance:</label><span>{distanceMiles?.toFixed(2)} miles</span></div>
      <div className="trip-group"><label>Number of Riders:</label><span>{numberOfRiders || 1} {(numberOfRiders || 1) === 1 ? 'person' : 'people'}</span></div>
      <div className="trip-group"><label>Wheelchair Vehicle:</label><span>{wheelchairRequired ? 'Required' : 'Not required'}</span></div>
      
      {/* Promo Code Section - STEP 3: RESTORE PROMO UI */}
      {true && (
      <div className="promo-section" style={{
        margin: '15px 0',
        padding: '15px',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        backgroundColor: '#f9f9f9'
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>🎟️ Promo Code</h4>
        
        {!appliedPromo ? (
          <>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Enter promo code"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                disabled={promoLoading}
              />
              <button
                onClick={() => validatePromoCode(promoCode)}
                disabled={promoLoading || !promoCode.trim()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: promoLoading ? '#ccc' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: promoLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {promoLoading ? '🔄' : 'Apply'}
              </button>
            </div>
            
            {/* Display promo message if any */}
            {promoMessage && (
              <div style={{ 
                marginTop: '10px', 
                padding: '8px', 
                borderRadius: '4px',
                fontSize: '13px',
                backgroundColor: promoMessage.includes('❌') ? '#f8d7da' : '#d4edda',
                color: promoMessage.includes('❌') ? '#721c24' : '#155724',
                border: `1px solid ${promoMessage.includes('❌') ? '#f5c6cb' : '#c3e6cb'}`
              }}>
                {promoMessage}
              </div>
            )}
          </>
        ) : (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '10px',
            backgroundColor: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '4px'
          }}>
            <div>
              <strong style={{ color: '#155724' }}>✅ {appliedPromo.code}</strong>
              <br />
              <small style={{ color: '#155724' }}>
                Saved £{appliedPromo.discount.toFixed(2)}
              </small>
            </div>
            <button
              onClick={removePromoCode}
              style={{
                padding: '4px 8px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Remove
            </button>
          </div>
        )}
      </div>
      )}

      {/* Fare Display with Promo Logic - STEP 2: RESTORE SIMPLE PROMO DISPLAY */}
      {appliedPromo ? (
        <div>
          <div className="trip-group">
            <label>Original Fare:</label>
            <span style={{ textDecoration: 'line-through', color: '#999' }}>
              £{originalFare?.toFixed(2)}
            </span>
          </div>
          <div className="trip-group">
            <label>Discount:</label>
            <span style={{ color: '#28a745' }}>
              -£{appliedPromo.discount?.toFixed(2)}
            </span>
          </div>
          <div className="trip-group" style={{ fontWeight: 'bold', color: '#28a745' }}>
            <label>Final Fare:</label>
            <span>£{discountedFare?.toFixed(2)}</span>
          </div>
        </div>
      ) : (
        <div className="trip-group"><label>Fare:</label><span>£{fare?.toFixed(2)}</span></div>
      )}
      
      <div className="trip-group"><label>Estimated Time:</label><span>{Math.round(tripTime)} mins</span></div>
      <div className="trip-group"><label>Rider:</label><span>{name}</span></div>
      <div className="trip-group"><label>Phone:</label><span>{phone}</span></div>

      {/* Payment Information Box */}
      <div className="payment-info-section" style={{
        margin: '20px 0',
        padding: '15px',
        border: '2px solid #28a745',
        borderRadius: '8px',
        backgroundColor: '#f8fff9'
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#28a745', display: 'flex', alignItems: 'center' }}>
          💳 Payment Information
        </h4>
        <div style={{ 
          fontSize: '14px', 
          lineHeight: '1.5',
          color: '#333'
        }}>
          <p style={{ margin: '5px 0', fontWeight: '500' }}>
            ✅ <strong>Cash payments accepted</strong>
          </p>
          <p style={{ margin: '5px 0', fontWeight: '500' }}>
            ✅ <strong>Card payments accepted</strong>
          </p>
          <p style={{ margin: '10px 0 5px 0', fontSize: '13px', color: '#666' }}>
            Your driver will handle payment at the end of your journey.
          </p>
        </div>
      </div>

      {bookingMode === 'scheduled' && (currentSelectedTime || scheduledTime) && (
        <div className="trip-group">
          <label>Scheduled Pickup:</label>
          <span>{new Date(currentSelectedTime || scheduledTime).toLocaleString()}</span>
        </div>
      )}

      {/* Alternative Times Dialog */}
      {showAlternativesDialog && (
        <div className="alternatives-overlay">
          <div className="alternatives-dialog">
            <h3>🕐 Alternative Time Slots</h3>
            <p>
              {alternativeTimes.length === 1 
                ? 'The requested time is not available, but here is an alternative slot:'
                : `The requested time is not available, but here are ${alternativeTimes.length} alternative slots based on existing bookings and route optimization:`
              }
            </p>
            
            {isBookingInProgress ? (
              <div className="booking-progress">
                <div className="spinner"></div>
                <p>Booking your ride...</p>
              </div>
            ) : (
              <div className="alternatives-list">
                {alternativeTimes.map((time, index) => {
                  const timeStr = time.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
                  const dateStr = time.toLocaleDateString('en-GB');
                  const altLabel = alternativeTimes.length === 2 
                    ? (index === 0 ? 'Earlier Slot' : 'Later Slot')
                    : `Alternative ${index + 1}`;
                  
                  return (
                    <div 
                      key={index} 
                      className="alternative-option"
                      onClick={() => !isBookingInProgress && bookAlternativeTime(time)}
                    >
                      <div className="alternative-icon">
                        {index === 0 ? '⏰' : '🕒'}
                      </div>
                      <div className="alternative-details">
                        <div className="alternative-label">{altLabel}: {timeStr}</div>
                        <div className="alternative-date">{dateStr}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {!isBookingInProgress && (
              <div className="alternatives-actions">
                <button 
                  className="cancel-button" 
                  onClick={() => {
                    setShowAlternativesDialog(false);
                    setNoScheduledDrivers(true);
                    setLoading(false);
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="different-time-button"
                  onClick={() => {
                    setShowAlternativesDialog(false);
                    navigate(-1); // Go back to rider map to select new time
                    showToast('Please select a different time slot', 'info');
                  }}
                >
                  Choose Different Time
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <button className="back-button" onClick={() => navigate(-1)}>← Back to Rider Map</button>

      {!driverConfirmed && (
        <button className="trip-button" onClick={checkDriverAvailability} disabled={loading}>
          {loading ? 'Checking Availability…' : (bookingMode === 'scheduled' ? 'Book Ride Later' : 'Book Ride Now')}
        </button>
      )}
    </div>
  );
};

export default TripDetailsScreen;
